import { useState, useEffect } from "react";
import { getStripeMode, setStripeMode } from "../../services/adminService";

interface StripeTogglePanelProps {
  className?: string;
}

export function StripeTogglePanel({ className = "" }: StripeTogglePanelProps) {
  const [mode, setMode] = useState<"test" | "live">("test");
  const [isLoading, setIsLoading] = useState(true);
  const [isSwitching, setIsSwitching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [pendingMode, setPendingMode] = useState<"test" | "live" | null>(null);

  useEffect(() => {
    async function load() {
      try {
        setIsLoading(true);
        const res = await getStripeMode();
        setMode(res.mode as "test" | "live");
      } catch (e) {
        setError(e instanceof Error ? e.message : "Failed to load Stripe mode");
      } finally {
        setIsLoading(false);
      }
    }
    load();
  }, []);

  const handleToggle = (newMode: "test" | "live") => {
    if (newMode === mode) return;
    if (newMode === "live") {
      setPendingMode("live");
      setShowConfirm(true);
    } else {
      doSwitch("test");
    }
  };

  const doSwitch = async (targetMode: "test" | "live") => {
    try {
      setIsSwitching(true);
      setError(null);
      setSuccess(null);
      setShowConfirm(false);
      setPendingMode(null);
      const res = await setStripeMode(targetMode);
      setMode(res.mode as "test" | "live");
      setSuccess(`Switched to ${res.mode} mode`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to switch Stripe mode");
    } finally {
      setIsSwitching(false);
    }
  };

  const isLive = mode === "live";

  if (isLoading) {
    return (
      <div className={`p-4 rounded-xl bg-surface-800/50 border border-surface-700/50 ${className}`}>
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center">
            <div className="animate-spin w-4 h-4 border-2 border-purple-400 border-t-transparent rounded-full" />
          </div>
          <span className="text-surface-400">Loading Stripe configuration...</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`p-4 rounded-xl bg-surface-800/50 border border-surface-700/50 ${className}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isLive ? "bg-red-500/20" : "bg-emerald-500/20"}`}>
            <svg
              className={`w-4 h-4 ${isLive ? "text-red-400" : "text-emerald-400"}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
              />
            </svg>
          </div>
          <div>
            <div className="text-sm font-medium text-surface-100">Stripe Mode</div>
            <div className="text-xs text-surface-400">Toggle between test and live payment processing</div>
          </div>
        </div>
        <div className={`px-2.5 py-1 rounded-full text-xs font-medium ${isLive ? "bg-red-500/20 text-red-400 border border-red-500/30" : "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30"}`}>
          {isLive ? "LIVE" : "TEST"}
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
          {error}
        </div>
      )}
      {success && (
        <div className="mb-4 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-sm">
          {success}
        </div>
      )}

      {/* Toggle Buttons */}
      <div className="flex gap-2">
        <button
          onClick={() => handleToggle("test")}
          disabled={isSwitching}
          className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
            !isLive
              ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/50"
              : "bg-surface-700/50 text-surface-400 border border-surface-600 hover:bg-surface-600/50 hover:text-surface-300"
          }`}
        >
          Test Mode
        </button>
        <button
          onClick={() => handleToggle("live")}
          disabled={isSwitching}
          className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
            isLive
              ? "bg-red-500/20 text-red-400 border border-red-500/50"
              : "bg-surface-700/50 text-surface-400 border border-surface-600 hover:bg-surface-600/50 hover:text-surface-300"
          }`}
        >
          Live Mode
        </button>
      </div>

      {isSwitching && (
        <div className="mt-3 flex items-center gap-2 text-sm text-surface-400">
          <div className="animate-spin w-4 h-4 border-2 border-purple-400 border-t-transparent rounded-full" />
          Switching...
        </div>
      )}

      {isLive && (
        <div className="mt-3 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-xs">
          <strong>Warning:</strong> Live mode processes real payments. All charges will be billed to customers.
        </div>
      )}

      {/* Confirmation Dialog */}
      {showConfirm && (
        <div className="mt-4 p-4 rounded-lg bg-surface-900 border border-red-500/50">
          <p className="text-sm text-surface-200 mb-3">
            Are you sure you want to switch to <strong className="text-red-400">live mode</strong>? This will process real payments.
          </p>
          <div className="flex gap-2 justify-end">
            <button
              onClick={() => { setShowConfirm(false); setPendingMode(null); }}
              className="px-3 py-1.5 rounded-lg text-sm bg-surface-700 text-surface-300 hover:bg-surface-600 transition-all"
            >
              Cancel
            </button>
            <button
              onClick={() => pendingMode && doSwitch(pendingMode)}
              className="px-3 py-1.5 rounded-lg text-sm bg-red-500 text-white hover:bg-red-600 transition-all"
            >
              Switch to Live
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
